'use client';
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Mic, Upload, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Language { id: string; name: string; native_name: string; dialects: string[]; }

const CATEGORIES = [
  'everyday_conversation','greetings','family','food','agriculture','traditional_knowledge',
  'proverbs','idioms','slang','music','religion','education','politics','civic_participation',
  'parliament','climate','health','business','technology','artificial_intelligence',
  'childrens_language','place_names','names','stories','oral_history',
];

const LICENCES = [
  { value: 'open_licence', label: 'Open Licence' },
  { value: 'public_domain', label: 'Public Domain' },
  { value: 'research_only', label: 'Research Use Only' },
  { value: 'commercial_ai_allowed', label: 'Commercial AI Training Allowed' },
  { value: 'non_commercial_only', label: 'Non-Commercial Only' },
];

const AGE_RANGES = ['Under 18', '18–25', '26–35', '36–50', '51–65', '65+', 'Prefer not to say'];

type TabType = 'text' | 'audio';

export default function MutauroContributePage() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const [tab, setTab] = useState<TabType>('text');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const supabase = createClient();

  const [textForm, setTextForm] = useState({
    language_id: searchParams?.get('lang') || '',
    dialect: '', original_text: '', english_translation: '',
    meaning_context: '', location: '', contributor_age_range: '',
    category: 'everyday_conversation', licence: 'open_licence',
  });

  const [audioForm, setAudioForm] = useState({
    language_id: searchParams?.get('lang') || '',
    dialect: '', spoken_sentence: '', english_translation: '',
    location: '', category: 'everyday_conversation',
    licence: 'open_licence', speaker_age_range: '', speaker_gender: '',
  });

  const [audioFile, setAudioFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    supabase.from('mutauro_languages').select('id,name,native_name,dialects').order('name')
      .then(({ data }) => { if (data) setLanguages(data); });
  }, []);

  const selectedLangText = languages.find(l => l.textForm?.language_id === l.id);
  const textLangDialects = languages.find(l => l.id === textForm.language_id)?.dialects || [];
  const audioLangDialects = languages.find(l => l.id === audioForm.language_id)?.dialects || [];

  async function submitText(e: React.FormEvent) {
    e.preventDefault();
    if (!textForm.language_id || !textForm.original_text) {
      setError('Language and original text are required.'); return;
    }
    setLoading(true); setError('');
    try {
      const { error: err } = await supabase.from('mutauro_text_submissions').insert({
        ...textForm,
        contributor_id: user?.id || null,
      });
      if (err) throw err;
      setSuccess(true);
      setTextForm({ language_id: '', dialect: '', original_text: '', english_translation: '',
        meaning_context: '', location: '', contributor_age_range: '', category: 'everyday_conversation', licence: 'open_licence' });
    } catch (e: any) {
      setError(e.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  }

  async function submitAudio(e: React.FormEvent) {
    e.preventDefault();
    if (!audioForm.language_id || !audioForm.spoken_sentence) {
      setError('Language and spoken sentence are required.'); return;
    }
    setLoading(true); setError('');
    try {
      let audioUrl = '';
      let durationSec = null;
      let fileFormat = '';
      let fileSizeBytes = null;

      if (audioFile) {
        fileFormat = audioFile.type;
        fileSizeBytes = audioFile.size;
        const path = `mutauro-audio/${Date.now()}-${audioFile.name}`;
        const { data: uploadData, error: uploadErr } = await supabase.storage
          .from('oral-submissions')
          .upload(path, audioFile, { upsert: false });
        if (!uploadErr && uploadData) {
          const { data: urlData } = supabase.storage.from('oral-submissions').getPublicUrl(path);
          audioUrl = urlData?.publicUrl || '';
        }
      }

      const { error: err } = await supabase.from('mutauro_audio_submissions').insert({
        ...audioForm,
        contributor_id: user?.id || null,
        audio_url: audioUrl || null,
        audio_format: fileFormat || null,
        audio_size_bytes: fileSizeBytes || null,
        audio_duration_seconds: durationSec,
      });
      if (err) throw err;
      setSuccess(true);
      setAudioForm({ language_id: '', dialect: '', spoken_sentence: '', english_translation: '',
        location: '', category: 'everyday_conversation', licence: 'open_licence', speaker_age_range: '', speaker_gender: '' });
      setAudioFile(null);
    } catch (e: any) {
      setError(e.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-200 transition-all";
  const inputStyle = { borderColor: 'var(--border)', background: 'var(--input)', color: 'var(--foreground)' };
  const labelCls = "block text-xs font-medium mb-1";
  const labelStyle = { color: 'var(--muted-foreground)' };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>Mutauro AI</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Contribute Language Data</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Help preserve Zimbabwe's indigenous languages by submitting text, audio or translations.
          </p>
        </div>

        {success && (
          <div className="mb-5 flex items-center gap-3 p-4 rounded-xl border" style={{ background: '#1A6B3A10', borderColor: '#1A6B3A40' }}>
            <CheckCircle size={18} style={{ color: '#1A6B3A' }} />
            <div>
              <div className="text-sm font-medium" style={{ color: '#1A6B3A' }}>Submission received!</div>
              <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Your data will be reviewed by our AI and human validators.</div>
            </div>
            <button onClick={() => setSuccess(false)} className="ml-auto"><X size={14} style={{ color: 'var(--muted-foreground)' }} /></button>
          </div>
        )}

        {error && (
          <div className="mb-5 flex items-center gap-3 p-4 rounded-xl border" style={{ background: '#CC000010', borderColor: '#CC000040' }}>
            <AlertCircle size={18} style={{ color: '#CC0000' }} />
            <span className="text-sm" style={{ color: '#CC0000' }}>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><X size={14} style={{ color: 'var(--muted-foreground)' }} /></button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'var(--secondary)' }}>
          {([['text', FileText, 'Text Submission'], ['audio', Mic, 'Audio Submission']] as const).map(([t, Icon, label]) => (
            <button key={t} onClick={() => setTab(t as TabType)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: tab === t ? 'var(--card)' : 'transparent',
                color: tab === t ? 'var(--foreground)' : 'var(--muted-foreground)',
                boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* Text Form */}
        {tab === 'text' && (
          <form onSubmit={submitText} className="rounded-xl border p-6 space-y-4" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} style={labelStyle}>Language *</label>
                <select className={inputCls} style={inputStyle} value={textForm.language_id}
                  onChange={e => setTextForm(f => ({ ...f, language_id: e.target.value, dialect: '' }))}>
                  <option value="">Select language…</option>
                  {languages.map(l => <option key={l.id} value={l.id}>{l.name}{l.native_name ? ` (${l.native_name})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Dialect</label>
                <select className={inputCls} style={inputStyle} value={textForm.dialect}
                  onChange={e => setTextForm(f => ({ ...f, dialect: e.target.value }))}>
                  <option value="">Select dialect…</option>
                  {textLangDialects.map(d => <option key={d} value={d}>{d}</option>)}
                  <option value="other">Other / Not listed</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Original Text *</label>
              <textarea className={inputCls} style={inputStyle} rows={3} placeholder="Enter text in the indigenous language…"
                value={textForm.original_text} onChange={e => setTextForm(f => ({ ...f, original_text: e.target.value }))} />
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>English Translation</label>
              <textarea className={inputCls} style={inputStyle} rows={2} placeholder="English translation…"
                value={textForm.english_translation} onChange={e => setTextForm(f => ({ ...f, english_translation: e.target.value }))} />
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Meaning / Context</label>
              <textarea className={inputCls} style={inputStyle} rows={2} placeholder="Cultural context, usage notes…"
                value={textForm.meaning_context} onChange={e => setTextForm(f => ({ ...f, meaning_context: e.target.value }))} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} style={labelStyle}>Category</label>
                <select className={inputCls} style={inputStyle} value={textForm.category}
                  onChange={e => setTextForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Location</label>
                <input className={inputCls} style={inputStyle} placeholder="e.g. Bulilima, Matabeleland"
                  value={textForm.location} onChange={e => setTextForm(f => ({ ...f, location: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Your Age Range</label>
                <select className={inputCls} style={inputStyle} value={textForm.contributor_age_range}
                  onChange={e => setTextForm(f => ({ ...f, contributor_age_range: e.target.value }))}>
                  <option value="">Prefer not to say</option>
                  {AGE_RANGES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Data Licence</label>
              <select className={inputCls} style={inputStyle} value={textForm.licence}
                onChange={e => setTextForm(f => ({ ...f, licence: e.target.value }))}>
                {LICENCES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>

            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                By submitting, you confirm you have the right to share this content under the selected licence. Your submission will be reviewed by AI and human validators before publication.
              </p>
              <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{ background: '#CC0000', color: '#fff' }}>
                {loading ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                {loading ? 'Submitting…' : 'Submit Text'}
              </button>
            </div>
          </form>
        )}

        {/* Audio Form */}
        {tab === 'audio' && (
          <form onSubmit={submitAudio} className="rounded-xl border p-6 space-y-4" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} style={labelStyle}>Language *</label>
                <select className={inputCls} style={inputStyle} value={audioForm.language_id}
                  onChange={e => setAudioForm(f => ({ ...f, language_id: e.target.value, dialect: '' }))}>
                  <option value="">Select language…</option>
                  {languages.map(l => <option key={l.id} value={l.id}>{l.name}{l.native_name ? ` (${l.native_name})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Dialect</label>
                <select className={inputCls} style={inputStyle} value={audioForm.dialect}
                  onChange={e => setAudioForm(f => ({ ...f, dialect: e.target.value }))}>
                  <option value="">Select dialect…</option>
                  {audioLangDialects.map(d => <option key={d} value={d}>{d}</option>)}
                  <option value="other">Other / Not listed</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Spoken Sentence *</label>
              <textarea className={inputCls} style={inputStyle} rows={2} placeholder="Write what is spoken in the recording…"
                value={audioForm.spoken_sentence} onChange={e => setAudioForm(f => ({ ...f, spoken_sentence: e.target.value }))} />
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>English Translation</label>
              <input className={inputCls} style={inputStyle} placeholder="English translation of the spoken sentence…"
                value={audioForm.english_translation} onChange={e => setAudioForm(f => ({ ...f, english_translation: e.target.value }))} />
            </div>

            {/* Audio Upload */}
            <div>
              <label className={labelCls} style={labelStyle}>Audio File (WAV, MP3, M4A, FLAC)</label>
              <div
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:border-red-300"
                style={{ borderColor: audioFile ? '#1A6B3A' : 'var(--border)', background: audioFile ? '#1A6B3A08' : 'var(--secondary)' }}
                onClick={() => fileInputRef.current?.click()}>
                <input ref={fileInputRef} type="file" accept=".wav,.mp3,.m4a,.flac,audio/*" className="hidden"
                  onChange={e => setAudioFile(e.target.files?.[0] || null)} />
                {audioFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle size={18} style={{ color: '#1A6B3A' }} />
                    <span className="text-sm font-medium" style={{ color: '#1A6B3A' }}>{audioFile.name}</span>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>({(audioFile.size / 1024).toFixed(0)} KB)</span>
                    <button type="button" onClick={e => { e.stopPropagation(); setAudioFile(null); }}>
                      <X size={14} style={{ color: 'var(--muted-foreground)' }} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload size={24} className="mx-auto mb-2" style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Click to upload audio file</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>WAV · MP3 · M4A · FLAC</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} style={labelStyle}>Category</label>
                <select className={inputCls} style={inputStyle} value={audioForm.category}
                  onChange={e => setAudioForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Location</label>
                <input className={inputCls} style={inputStyle} placeholder="e.g. Binga, Matabeleland North"
                  value={audioForm.location} onChange={e => setAudioForm(f => ({ ...f, location: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Speaker Age Range</label>
                <select className={inputCls} style={inputStyle} value={audioForm.speaker_age_range}
                  onChange={e => setAudioForm(f => ({ ...f, speaker_age_range: e.target.value }))}>
                  <option value="">Prefer not to say</option>
                  {AGE_RANGES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} style={labelStyle}>Speaker Gender (optional)</label>
                <select className={inputCls} style={inputStyle} value={audioForm.speaker_gender}
                  onChange={e => setAudioForm(f => ({ ...f, speaker_gender: e.target.value }))}>
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="non_binary">Non-binary</option>
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Data Licence</label>
                <select className={inputCls} style={inputStyle} value={audioForm.licence}
                  onChange={e => setAudioForm(f => ({ ...f, licence: e.target.value }))}>
                  {LICENCES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>

            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                By submitting, you confirm you have the right to share this recording under the selected licence. Audio is stored securely and reviewed before publication.
              </p>
              <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                style={{ background: '#CC0000', color: '#fff' }}>
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Mic size={15} />}
                {loading ? 'Submitting…' : 'Submit Audio'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
